/** @format */

import {useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {format} from 'date-fns';

import {buildMetricInput, selectActiveMediaplan} from 'pages/HomeOverview/helpers/buildMetricInput';

import {calcMetricProgress} from 'features/mediaplan-progress/helpers/calcMetricProgress';
import {toPlanPeriodInput} from 'features/mediaplan-progress/helpers/toPlanPeriodInput';
import {type MetricProgress} from 'features/mediaplan-progress/types';

import {type CardBodyCompany, type ViewMode} from 'pages/HomeOverview/types';

import {KpiSection} from './KpiSection';
import {type KpiChartRow} from './KpiSection/KpiCharts';
import {KpiTable} from './KpiSection/KpiTable';
import {NoMediaplanSummary} from './NoMediaplanSummary';
import {type SpendChartData, SpendSection} from './SpendSection';
import {buildKpiRows} from './buildKpiRows';
import {buildChartData} from './buildChartData';
import {spendChartHeight} from './chartHeights';
import {getPlanPeriodLabel} from './getPlanPeriodLabel';
import {shouldShowForecast} from './shouldShowForecast';
import {CenterDivider, KpiColumn, MetricsRoot, SpendColumn} from './styles';

interface Props {
  viewMode: ViewMode;
  company: CardBodyCompany;
  now: Date;
  // Passthrough — Phase 7 wires the actual "setup mediaplan" navigation.
  onSetupMediaplan?: () => void;
}

// MetricProgress без данных — все нули. Используется как fallback, когда по расходу
// ('budget') нет input (нет графика расхода / плана) — SpendSection всегда рендерится.
const ZERO_PROGRESS: MetricProgress = {
  planForPeriod: 0,
  planToDate: 0,
  factToDate: 0,
  averageFactPerDay: 0,
  forecastAtEnd: 0,
};

const noop = () => {};

/**
 * Card body: computes KPI + Spend metric progress (and dynamic chart data) from the
 * lazily-loaded CardBodyCompany and lays them out as two columns — KPIs (left) and
 * Spend (right), split by a center divider.
 *
 * Summary is the default view (MetricProgress only); dynamic mode additionally renders
 * MultilineCharts built from the same MetricInput via buildChartData.
 */
function MetricsBlock({viewMode, company, now, onSetupMediaplan}: Props) {
  const {t} = useTranslation();
  const today = format(now, 'yyyy-MM-dd');

  const hasMediaplan = company.mediaplans.length > 0;
  // Charts are only consumed by the dynamic view; summary (KpiTable) discards them, so skip the
  // heavy buildChartData (forecast + point arrays) per KPI and for spend when not in dynamic mode.
  const isDynamic = viewMode === 'dynamic';

  // Resolve the active plan/period ONCE per card and thread it into every metric (buildKpiRows /
  // buildMetricInput) instead of re-deriving it for each KPI + budget.
  const active = useMemo(() => selectActiveMediaplan(company, today), [company, today]);

  // «План на …» label tracks the ACTIVE plan's period: month name for a monthly plan, date range
  // for a flight/weekly. No plan → current month (the label row isn't shown without a plan anyway).
  // getPlanPeriodLabel wants the PlanPeriodInput form (normalized repeat).
  const planPeriodLabel = getPlanPeriodLabel(
    active ? {plan: toPlanPeriodInput(active.plan), period: active.period} : null,
    now,
  );

  const kpiRows = useMemo(
    () => buildKpiRows(company, today, isDynamic, active),
    [company, today, isDynamic, active],
  );

  // Расход-график в динамике заполняет всю правую колонку → его высота отслеживает
  // KPI-строки левой колонки. В summary график не рендерится.
  const spendHeight = useMemo(() => {
    if (viewMode !== 'dynamic' || !hasMediaplan) {
      return undefined;
    }
    return spendChartHeight(kpiRows.map(row => row.kind));
  }, [viewMode, hasMediaplan, kpiRows]);

  const {spendProgress, spendChart, spendShowForecast} = useMemo(() => {
    const input = buildMetricInput({company, metric: 'budget', today, active});
    if (!input) {
      return {spendProgress: ZERO_PROGRESS, spendChart: undefined, spendShowForecast: false};
    }
    const showForecast = shouldShowForecast(input);
    // Spend chart is dynamic-only too (summary shows the spend row in KpiTable, no chart).
    const chart: SpendChartData | undefined = isDynamic
      ? (buildChartData(input, 'spend', 'Расход', today, spendHeight, showForecast) ?? undefined)
      : undefined;
    return {
      spendProgress: calcMetricProgress(input),
      spendChart: chart,
      spendShowForecast: showForecast,
    };
  }, [company, today, spendHeight, isDynamic, active]);

  // Summary + есть медиаплан → одна full-width таблица: KPI-строки + Расход строкой
  // (Figma 10284-127902). Иначе 6 колонок зажимаются в половину карточки и «Выполнение»/
  // «Прогноз» обрезаются. Расход — cost-метрика (lowerIsBetter), деньги в ₽, ведущая иконка ₽.
  if (viewMode === 'summary' && hasMediaplan) {
    const spendRow: KpiChartRow = {
      chartId: 'budget',
      label: t('Расход'),
      kind: 'additive',
      progress: spendProgress,
      lowerIsBetter: true,
      showForecast: spendShowForecast,
      unit: '₽',
      unitNeedsSpace: true,
      iconName: 'currency_ruble',
    };
    const tableRows = [...kpiRows, spendRow];
    return <KpiTable rows={tableRows} planPeriodLabel={planPeriodLabel} />;
  }

  // Summary без медиаплана → узкий бокс: мини-таблица Расхода (Факт/Прогноз, плана нет)
  // + штриховка-CTA «Настроить медиаплан» под ней (Figma 10286:128462).
  if (viewMode === 'summary' && !hasMediaplan) {
    return (
      <NoMediaplanSummary
        progress={spendProgress}
        onSetup={onSetupMediaplan ?? noop}
        showForecast={spendShowForecast}
      />
    );
  }

  // Dynamic → две колонки (KPI/графики | блок Расхода).
  return (
    <MetricsRoot>
      <KpiColumn>
        <KpiSection
          viewMode={viewMode}
          rows={kpiRows}
          hasMediaplan={hasMediaplan}
          planPeriodLabel={planPeriodLabel}
          onSetupMediaplan={onSetupMediaplan ?? noop}
        />
      </KpiColumn>

      <CenterDivider />

      <SpendColumn>
        <SpendSection
          progress={spendProgress}
          chart={spendChart}
          planPeriodLabel={planPeriodLabel}
          showForecast={spendShowForecast}
        />
      </SpendColumn>
    </MetricsRoot>
  );
}

export {MetricsBlock};
